import {
  Box,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography
} from "@material-ui/core";

import { v4CatalogChanged, v4UnitPriceComparison } from "../v4CatalogReference";
import { v4Date, v4Money, v4Quantity, v4ReadableText } from "../v4Explanation";
import { v4ReferenceState } from "../v4ReferenceState";
import { isV4ExplicitVector } from "../v4Selection";

const titles = {
  maintain: "Manter os preços atuais",
  price_hypothesis: "Testar uma alternativa de preço",
  abstain: "Não recomendar uma mudança de preço"
};
const unavailable = {
  rejected: "Nenhuma recomendação foi aprovada nesta rodada",
  insufficient_data: "Dados insuficientes",
  pending: "Aguardando decisão",
  error: "Decisão indisponível"
};

export default function V4DecisionSummary({ suggestion, statusLabel, row }) {
  const ready = suggestion.status === "ready";
  const catalogChanged = v4CatalogChanged(suggestion, row);
  const unitComparison = v4UnitPriceComparison(suggestion);
  const reference = v4ReferenceState(suggestion);
  const signals = Array.isArray(suggestion.signals) ? suggestion.signals : [];
  const market = suggestion.market;
  const quotes = Array.isArray(market?.competitors?.quotes)
    ? market.competitors.quotes
    : [];
  const rationale = [
    suggestion.rationale,
    suggestion.reason_short,
    suggestion.explanation?.summary
  ].find(v => typeof v === "string" && v.trim());
  const rows = signals.map(s => ({
    label: s.label,
    value: v4Quantity(s.value),
    unit: s.signal_id?.includes("Shelf Life")
      ? "dias informados"
      : "unidade da análise de compras",
    date: s.target_day || s.target_date,
    detail: s.signal_id?.includes("Shelf Life")
      ? "Vida útil informada"
      : `Equivalência física não confirmada${
          s.cited_by_agent ? " · Citado pelo agente" : ""
        }`
  }));
  const stock = suggestion.stock_reconciliation;
  if (stock) {
    const stockUnit =
      stock.unit === "producer_PA_field_unit_unattested"
        ? "unidade da análise de compras (equivalência física não atestada)"
        : "unidade da fonte (equivalência física não confirmada)";
    rows.push({
      label: "Estoque físico",
      value: v4Quantity(stock.physical),
      unit: stockUnit,
      detail: "Equivalência física não confirmada"
    });
    rows.push({
      label: "Vendido ou reservado",
      value: v4Quantity(stock.registered_sold_or_reserved),
      unit: stockUnit,
      detail: "Pedidos registrados"
    });
    rows.push({
      label: "Físico menos pedidos",
      value: v4Quantity(stock.physical_less_orders),
      unit: stockUnit,
      detail: stock.stock_balance_reconciled
        ? "Saldo reconciliado na fonte"
        : "Saldo não reconciliado; não confirma ruptura ou sobra"
    });
    rows.push({
      label: "Disponível informado",
      value: v4Quantity(stock.summary_available),
      unit: stockUnit,
      detail: `Diferença para o saldo: ${v4Quantity(stock.difference)}`
    });
  }
  rows.push({
    label: "CMC",
    value: v4Money(suggestion.cmc_base_brl),
    unit: "R$/unidade base do catálogo",
    date: suggestion.cmc_source_date,
    detail: "Custo da mercadoria; não é preço de venda"
  });
  rows.push({
    label: "Perda prevista em reais",
    value: "Não disponível",
    unit: "R$",
    date: null,
    detail: "Unidade de descarte sem equivalência confirmada com CMC"
  });
  rows.push({
    label: "CEASA",
    value: v4Money(market?.ceasa?.price_brl),
    unit:
      market?.ceasa?.unit === "producer_Gold_normalized_base_unit"
        ? "R$/unidade base do produtor Gold"
        : "atacado / unidade base da fonte",
    date: market?.ceasa?.source_date,
    detail:
      market?.ceasa?.state === "stale"
        ? "Referência de atacado defasada"
        : "Referência de atacado"
  });
  if (quotes.length)
    quotes.forEach(q =>
      rows.push({
        label: q.label,
        value: v4Money(q.price_brl),
        unit: "R$/kg",
        date: null,
        detail: "Data da cotação e equivalência não confirmadas"
      })
    );
  else
    rows.push({
      label: "Concorrentes",
      value: "Indisponível",
      unit: "—",
      date: null,
      detail:
        market?.competitors?.state === "ambiguous"
          ? "Registros duplicados"
          : "Sem cotação válida"
    });
  return (
    <Box mb={3}>
      <Box
        display="flex"
        alignItems="center"
        flexWrap="wrap"
        mb={1}
        style={{ gap: 8 }}
      >
        <Typography component="h3" variant="h6">
          {ready
            ? suggestion.stance === "maintain" &&
              (catalogChanged || reference.expired)
              ? "Manter os preços da análise anterior"
              : titles[suggestion.stance] || "Decisão de preço"
            : unavailable[suggestion.status] || "Aguardando decisão"}
        </Typography>
        <Chip
          label={catalogChanged ? "Preços alterados" : statusLabel}
          size="small"
          variant="outlined"
        />
      </Box>
      <Typography variant="body2" color="textSecondary" paragraph>
        Análise de {v4Date(suggestion.generated_at)}
        {suggestion.expires_at
          ? ` · Válida até ${v4Date(suggestion.expires_at)}`
          : ""}
      </Typography>
      <Typography variant="body2" color="textSecondary" paragraph>
        V4 em modo de análise. A edição e a aplicação de preços continuam pelo
        V3.
      </Typography>
      {catalogChanged ? (
        <Typography role="alert" variant="body2" paragraph>
          Os preços do catálogo mudaram desde esta análise. O parecer usa a
          tabela anterior e não recomenda manter os preços exibidos agora.
          Aguarde uma nova decisão antes de usar esta referência.
        </Typography>
      ) : null}
      {ready ? (
        <Typography variant="body2" paragraph>
          Referência de cálculo V4:{" "}
          {v4Money(suggestion.suggested_anchor_base_brl)}.
          {isV4ExplicitVector(suggestion)
            ? " Preço por unidade da maior embalagem. Os preços preservam os descontos relativos atuais de cada porção."
            : suggestion.stance === "maintain"
            ? " Média ponderada dos preços por unidade das embalagens."
            : " Âncora para distribuir os preços das embalagens."}
          {unitComparison
            ? ` Na embalagem de ${v4Quantity(
                unitComparison.portion_base
              )} porções base${
                isV4ExplicitVector(suggestion)
                  ? " usada como referência V4"
                  : " usada para comparar com o P. Atual"
              }: ${v4Money(
                unitComparison.current_unit_price_brl
              )} por unidade na análise → ${v4Money(
                unitComparison.suggested_unit_price_brl
              )} por unidade sugerida.`
            : ""}
        </Typography>
      ) : null}
      {reference.expired ||
      (suggestion.reference_retained &&
        (reference.updating || reference.failed)) ? (
        <Typography
          role={reference.failed || reference.expired ? "alert" : "status"}
          variant="body2"
          paragraph
        >
          {reference.expired
            ? "Referência vencida; aguarde uma decisão atual antes de usar o preço. "
            : ""}
          {reference.updating
            ? "Atualização em andamento. "
            : reference.failed
            ? "A última atualização falhou. "
            : ""}
          O parecer abaixo pertence à análise de{" "}
          {v4Date(suggestion.generated_at)}.
        </Typography>
      ) : null}
      <Typography
        component="h4"
        variant="subtitle1"
        style={{ fontWeight: 600 }}
        gutterBottom
      >
        Parecer do agente
      </Typography>
      {ready &&
      (isV4ExplicitVector(suggestion) || suggestion.stance === "maintain") &&
      suggestion.requires_human_review ? (
        <Typography role="alert" variant="body2" paragraph>
          {isV4ExplicitVector(suggestion)
            ? "A tabela atual apresenta conflito de desconto por tamanho de embalagem. Os preços V4 preservam essa relação; confira os descontos antes de qualquer edição manual."
            : "A tabela atual não cumpre o desconto por tamanho de embalagem. Confira essa relação ao analisar a recomendação de manter."}
        </Typography>
      ) : null}
      <Typography
        variant="body1"
        paragraph
        style={{ whiteSpace: "pre-line", maxWidth: "80ch" }}
      >
        {v4ReadableText(rationale) ||
          "O agente não forneceu um parecer nesta rodada."}
      </Typography>
      {!ready ? (
        <Typography variant="body2" paragraph>
          O P. Final continua disponível para edição manual.
        </Typography>
      ) : null}
      {ready ? (
        <>
          <Typography
            component="h4"
            variant="subtitle1"
            style={{ fontWeight: 600 }}
            gutterBottom
          >
            Dados usados na decisão
          </Typography>
          <Box style={{ overflowX: "auto" }}>
            <Table
              size="small"
              aria-label="Dados usados na decisão V4"
              style={{ minWidth: 740 }}
            >
              <TableHead>
                <TableRow>
                  <TableCell style={{ minWidth: 140 }}>Indicador</TableCell>
                  <TableCell align="right">Valor</TableCell>
                  <TableCell>Unidade</TableCell>
                  <TableCell style={{ whiteSpace: "nowrap" }}>
                    Dia-alvo
                  </TableCell>
                  <TableCell>Qualidade do dado</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((r, i) => (
                  <TableRow key={`${r.label}-${i}`}>
                    <TableCell style={{ overflowWrap: "normal" }}>
                      {r.label}
                    </TableCell>
                    <TableCell align="right" style={{ whiteSpace: "nowrap" }}>
                      {r.value}
                    </TableCell>
                    <TableCell>{r.unit}</TableCell>
                    <TableCell style={{ whiteSpace: "nowrap" }}>
                      {r.date ? v4Date(r.date) : "Não informado"}
                    </TableCell>
                    <TableCell>{r.detail}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
          <Typography
            variant="body2"
            color="textSecondary"
            style={{ marginTop: 8 }}
          >
            Previsão, projeção e realizado são medidas distintas. Descarte
            previsto zero não confirma ausência de perdas.
          </Typography>
        </>
      ) : null}
    </Box>
  );
}
